import { runSimulation } from './app';
import { SimulationLoadoutStatsOutput, SimulationRunOptions, WeaponMatrixInput, WeaponMatrixOutput } from './types';
import { maybeYieldForSimulationChunk, throwIfSimulationAborted } from './simulation-control';

const PLATEAU_STEPS_REQUIRED = 3;
const PLATEAU_PERCENT_TOLERANCE = 1;
const MATRIX_YIELD_INTERVAL = 1;

function getScenarioToken(scenarioId: string): string | null {
    if (scenarioId === 'base') return null;
    const token = scenarioId.split('-').slice(0, -1).join('-');
    return token || null;
}

function areDiceArraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

function arePercentsWithinTolerance(a: number, b: number): boolean {
    return Math.abs(a - b) <= PLATEAU_PERCENT_TOLERANCE;
}

function areLoadoutOutputsEquivalent(a: SimulationLoadoutStatsOutput[], b: SimulationLoadoutStatsOutput[]): boolean {
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
        const left = a[i];
        const right = b[i];
        if (
            !arePercentsWithinTolerance(left.chanceToWound, right.chanceToWound) ||
            !arePercentsWithinTolerance(left.chanceToHit, right.chanceToHit) ||
            !arePercentsWithinTolerance(left.chanceToFullHit, right.chanceToFullHit) ||
            !arePercentsWithinTolerance(left.chanceToFullMiss, right.chanceToFullMiss) ||
            !areDiceArraysEqual(left.diceRolledOnFullHit, right.diceRolledOnFullHit)
        ) {
            return false;
        }
    }

    return true;
}

export async function runWeaponMatrixSimulation(input: WeaponMatrixInput, options: SimulationRunOptions = {}): Promise<WeaponMatrixOutput> {
    const cells: WeaponMatrixOutput['cells'] = [];
    const remainingScenarios = input.scenarios.slice(1);
    let completedSimulations = 0;

    const baseScenario = input.scenarios[0];
    if (baseScenario) {
        for (const target of input.targets) {
            throwIfSimulationAborted(options.signal);
            const result = await runSimulation({
                loadouts: baseScenario.loadouts,
                primordial: {
                    toHitValue: input.toHitValue,
                    aeonTrespassValue: target.aeonTrespassValue,
                    aeonTrespassValues: target.aeonTrespassValues,
                },
                kratosPool: baseScenario.kratosPool,
                config: input.config,
            }, options);
            throwIfSimulationAborted(options.signal);

            cells.push({
                scenarioId: baseScenario.id,
                bpLevel: target.bpLevel,
                kratosPool: baseScenario.kratosPool,
                loadoutOutputs: result.loadoutOutputs,
            });
            completedSimulations++;
            const yieldPromise = maybeYieldForSimulationChunk(completedSimulations, options, MATRIX_YIELD_INTERVAL);
            if (yieldPromise) {
                await yieldPromise;
            }
        }
    }

    let index = 0;
    while (index < remainingScenarios.length) {
        const scenario = remainingScenarios[index];
        throwIfSimulationAborted(options.signal);
        const token = getScenarioToken(scenario.id);
        const tokenScenarios = [scenario];
        let cursor = index + 1;
        while (cursor < remainingScenarios.length && getScenarioToken(remainingScenarios[cursor].id) === token) {
            tokenScenarios.push(remainingScenarios[cursor]);
            cursor++;
        }

        let previousTargetOutputs: Map<string, SimulationLoadoutStatsOutput[]> | null = null;
        let stableStepCount = 0;

        for (let stepIndex = 0; stepIndex < tokenScenarios.length; stepIndex++) {
            const tokenScenario = tokenScenarios[stepIndex];
            const currentTargetOutputs = new Map<string, SimulationLoadoutStatsOutput[]>();
            let isStableAcrossTargets = previousTargetOutputs !== null;

            for (const target of input.targets) {
                throwIfSimulationAborted(options.signal);
                const result = await runSimulation({
                    loadouts: tokenScenario.loadouts,
                    primordial: {
                        toHitValue: input.toHitValue,
                        aeonTrespassValue: target.aeonTrespassValue,
                        aeonTrespassValues: target.aeonTrespassValues,
                    },
                    kratosPool: tokenScenario.kratosPool,
                    config: input.config,
                }, options);
                throwIfSimulationAborted(options.signal);

                currentTargetOutputs.set(target.bpLevel, result.loadoutOutputs);
                cells.push({
                    scenarioId: tokenScenario.id,
                    bpLevel: target.bpLevel,
                    kratosPool: tokenScenario.kratosPool,
                    loadoutOutputs: result.loadoutOutputs,
                });
                completedSimulations++;
                const yieldPromise = maybeYieldForSimulationChunk(completedSimulations, options, MATRIX_YIELD_INTERVAL);
                if (yieldPromise) {
                    await yieldPromise;
                }

                if (previousTargetOutputs) {
                    const previousOutputs = previousTargetOutputs.get(target.bpLevel);
                    if (!previousOutputs || !areLoadoutOutputsEquivalent(previousOutputs, result.loadoutOutputs)) {
                        isStableAcrossTargets = false;
                    }
                }
            }

            if (token !== 'rouse' && isStableAcrossTargets) {
                stableStepCount++;
            } else {
                stableStepCount = 1;
            }

            if (token !== 'rouse' && stableStepCount >= PLATEAU_STEPS_REQUIRED && stepIndex < tokenScenarios.length - 1) {
                for (let remainingIndex = stepIndex + 1; remainingIndex < tokenScenarios.length; remainingIndex++) {
                    const plateauScenario = tokenScenarios[remainingIndex];
                    for (const target of input.targets) {
                        const plateauOutputs = currentTargetOutputs.get(target.bpLevel);
                        if (!plateauOutputs) continue;
                        cells.push({
                            scenarioId: plateauScenario.id,
                            bpLevel: target.bpLevel,
                            kratosPool: plateauScenario.kratosPool,
                            loadoutOutputs: plateauOutputs,
                        });
                    }
                }
                break;
            }

            previousTargetOutputs = currentTargetOutputs;
        }

        index = cursor;
    }

    return {
        primordialName: input.primordialName,
        primordialLevel: input.primordialLevel,
        toHitValue: input.toHitValue,
        targets: input.targets,
        scenarios: input.scenarios,
        cells,
    };
}
